import type {BoardLane} from './board-lanes'
import {isTimedStartingSoon, minutesUntilTimedStart, resolveNowHour} from './schedule-urgency'
import type {TerminalRowEntry} from './terminal-jobs'
import {toGlobalSlotIndexOrLast} from './timeline-view'
import type {DispatchTimelineData, ScheduleDate, ViewRange} from './types'
import {SLOTS_PER_DAY, WORK_DAY_START_HOUR} from './work-day'

export type HorizonContext = {
    visibleDays: DispatchTimelineData['visibleDays']
    viewRange: ViewRange
    nowHour: number
    scheduleWindowStart?: string
}

export type OperationalRank = {
    tier: number
    subRank: number
    tieBreaker: number
}

const DISPATCHER_ACTION_ORDER: Record<string, number> = {
    assignment: 0,
    notification: 1,
    none: 2,
}

export function buildHorizonContext(
    data: DispatchTimelineData,
    viewRange: ViewRange,
): HorizonContext {
    return {
        visibleDays: data.visibleDays ?? [data.scheduleDate],
        viewRange,
        nowHour: resolveNowHour(),
        scheduleWindowStart: data.scheduleWindowStart,
    }
}

export function compareOperationalRank(left: OperationalRank, right: OperationalRank): number {
    return (
        left.tier - right.tier || left.subRank - right.subRank || left.tieBreaker - right.tieBreaker
    )
}

const CALENDAR_DAY_MS = 24 * 60 * 60 * 1000
const MONTH_INDEXES: Record<string, number> = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
}

function calendarDayNumber(value: string | undefined): number | null {
    if (!value) return null

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (!match) return null

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    const timestamp = Date.UTC(year, month - 1, day)
    const parsed = new Date(timestamp)

    if (
        parsed.getUTCFullYear() !== year ||
        parsed.getUTCMonth() !== month - 1 ||
        parsed.getUTCDate() !== day
    ) {
        return null
    }

    return timestamp / CALENDAR_DAY_MS
}

function calendarDayNumberFromLabel(day: ScheduleDate | undefined): number | null {
    if (!day) return null

    const match = /([A-Za-z]{3,9})\s+(\d{1,2}),\s+(\d{4})$/.exec(day.label)
    if (!match) return null

    const month = MONTH_INDEXES[match[1]!.slice(0, 3).toLowerCase()]
    if (month == null) return null

    return calendarDayNumber(
        `${match[3]}-${String(month + 1).padStart(2, '0')}-${match[2]!.padStart(2, '0')}`,
    )
}

function startMinutes(value: string | null | undefined): number | null {
    if (!value) return null

    const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value)
    if (!match) return null

    const hours = Number(match[1])
    const minutes = Number(match[2])
    const seconds = match[3] == null ? 0 : Number(match[3])
    if (hours > 23 || minutes > 59 || seconds > 59) return null

    return hours * 60 + minutes
}

function fallbackStartMinutes(entry: TerminalRowEntry): number {
    const slotIndex = entry.job.span?.startSlotIndex
    if (slotIndex == null || !Number.isFinite(slotIndex)) {
        return Number.POSITIVE_INFINITY
    }

    const slotInDay = ((slotIndex % SLOTS_PER_DAY) + SLOTS_PER_DAY) % SLOTS_PER_DAY
    return (WORK_DAY_START_HOUR + slotInDay) * 60
}

function effectiveScheduleDay(entry: TerminalRowEntry, context: HorizonContext): number {
    const rawDay = calendarDayNumber(entry.job.schedule?.startDate)
    if (rawDay != null) return rawDay

    const anchorDay = calendarDayNumber(context.scheduleWindowStart)
    if (anchorDay != null) return anchorDay + entry.viewDayOffset

    const visibleDay = calendarDayNumberFromLabel(context.visibleDays?.[entry.viewDayOffset])
    return visibleDay ?? entry.viewDayOffset
}

function compareText(left: string | undefined, right: string | undefined): number {
    const leftValue = left ?? ''
    const rightValue = right ?? ''
    return leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0
}

/** Orders terminal rows by their most precise available schedule and stable identity. */
export function compareTerminalRowSchedule(
    left: TerminalRowEntry,
    right: TerminalRowEntry,
    context: HorizonContext,
): number {
    const dayComparison = effectiveScheduleDay(left, context) - effectiveScheduleDay(right, context)
    if (dayComparison !== 0) return dayComparison

    const leftMinutes = startMinutes(left.job.schedule?.startTime) ?? fallbackStartMinutes(left)
    const rightMinutes = startMinutes(right.job.schedule?.startTime) ?? fallbackStartMinutes(right)
    if (leftMinutes !== rightMinutes) return leftMinutes - rightMinutes

    return (
        compareText(left.job.sourceJobId, right.job.sourceJobId) ||
        compareText(left.job.id, right.job.id) ||
        compareText(left.job.jobCode, right.job.jobCode)
    )
}

export function getNeedsActionRank(
    entry: TerminalRowEntry,
    context: HorizonContext,
): OperationalRank {
    const {job, display, operational, viewDayOffset, isContinuationDay} = entry
    const tieBreaker = toGlobalSlotIndexOrLast(job.span)
    const day = context.visibleDays?.[viewDayOffset]
    const isToday = day?.isToday ?? false
    const nowHour = context.nowHour
    const timedStartOptions = {isToday}
    const hasShortage = display.missingLabels.length > 0
    const dispatcherOwned =
        operational.actionOwner === 'dispatcher' && operational.nextAction !== 'none'

    if (
        isContinuationDay &&
        !hasShortage &&
        operational.nextAction !== 'assignment' &&
        operational.nextAction !== 'notification'
    ) {
        return {tier: 6, subRank: viewDayOffset, tieBreaker}
    }

    if (
        job.scheduleKind === 'timed' &&
        isToday &&
        isTimedStartingSoon(job, viewDayOffset, nowHour, timedStartOptions)
    ) {
        const minutesUntil =
            minutesUntilTimedStart(job, viewDayOffset, nowHour, timedStartOptions) ??
            Number.POSITIVE_INFINITY
        return {tier: 1, subRank: Math.max(0, minutesUntil), tieBreaker}
    }

    if (isToday && hasShortage) {
        return {
            tier: 2,
            subRank: -display.missingLabels.length,
            tieBreaker,
        }
    }

    if (isToday && dispatcherOwned) {
        return {
            tier: 3,
            subRank: DISPATCHER_ACTION_ORDER[operational.nextAction] ?? 9,
            tieBreaker,
        }
    }

    if (
        display.spansMultipleDays &&
        hasShortage &&
        (isContinuationDay || (job.span?.continuesIntoNextDay ?? false))
    ) {
        return {tier: 4, subRank: viewDayOffset, tieBreaker}
    }

    if (!isToday) {
        return {tier: 5, subRank: viewDayOffset, tieBreaker}
    }

    if (isContinuationDay) {
        return {tier: 4, subRank: viewDayOffset, tieBreaker}
    }

    return {tier: 5, subRank: viewDayOffset, tieBreaker}
}

export function getNotifyDriversRank(
    entry: TerminalRowEntry,
    context: HorizonContext,
): OperationalRank {
    const {job, viewDayOffset} = entry
    const tieBreaker = toGlobalSlotIndexOrLast(job.span)
    const day = context.visibleDays?.[viewDayOffset]
    const isToday = day?.isToday ?? false
    const nowHour = context.nowHour
    const startingSoon = isTimedStartingSoon(job, viewDayOffset, nowHour, {isToday})

    if (startingSoon) {
        const minutesUntil =
            minutesUntilTimedStart(job, viewDayOffset, nowHour, {isToday}) ??
            Number.POSITIVE_INFINITY
        return {tier: 1, subRank: Math.max(0, minutesUntil), tieBreaker}
    }

    if (isToday) {
        return {tier: 3, subRank: tieBreaker, tieBreaker}
    }

    return {tier: 4, subRank: viewDayOffset, tieBreaker}
}

export function getDispatchedRank(entry: TerminalRowEntry): OperationalRank {
    return {
        tier: 1,
        subRank: entry.viewDayOffset,
        tieBreaker: toGlobalSlotIndexOrLast(entry.job.span),
    }
}

function rankForLane(
    entry: TerminalRowEntry,
    lane: BoardLane,
    context: HorizonContext,
): OperationalRank {
    switch (lane) {
        case 'needs_action':
            return getNeedsActionRank(entry, context)
        case 'notify_drivers':
            return getNotifyDriversRank(entry, context)
        case 'dispatched':
            return getDispatchedRank(entry)
    }
}

export function rankTerminalRows(
    entries: TerminalRowEntry[],
    lane: BoardLane,
    context: HorizonContext,
): TerminalRowEntry[] {
    return [...entries].sort((left, right) => {
        const operationalComparison = compareOperationalRank(
            rankForLane(left, lane, context),
            rankForLane(right, lane, context),
        )
        return operationalComparison || compareTerminalRowSchedule(left, right, context)
    })
}

export function rankTerminalEntriesByLane(
    entries: TerminalRowEntry[],
    context: HorizonContext,
): TerminalRowEntry[] {
    const needsAction = rankTerminalRows(
        entries.filter((entry) => entry.lane === 'needs_action'),
        'needs_action',
        context,
    )
    const notifyDrivers = rankTerminalRows(
        entries.filter((entry) => entry.lane === 'notify_drivers'),
        'notify_drivers',
        context,
    )
    const dispatched = rankTerminalRows(
        entries.filter((entry) => entry.lane === 'dispatched'),
        'dispatched',
        context,
    )

    return [...needsAction, ...notifyDrivers, ...dispatched]
}
